/* Modulo: Shoppable Video — scroll-snap rail, in-view muted autoplay,
   variant popover, /cart/add.js quick-add, and a video lightbox with focus trap.
   Vanilla IIFE. Theme-editor aware. Reduced-motion aware. No deps. */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── Cart-add (trimmed; mirrors theme-sections/modulo-section-featured-products.js) ─── */
  function cartAddUrl() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js';
  }
  function findCartDrawer() {
    return document.querySelector('cart-drawer, cart-drawer-component, [is="cart-drawer"], [data-cart-drawer]');
  }
  function openCartDrawer() {
    var drawer = findCartDrawer();
    if (!drawer) return;
    if (typeof drawer.open === 'function') drawer.open();
    else if (typeof drawer.show === 'function') drawer.show();
    else {
      var trigger = document.querySelector('[aria-controls*="cart"]');
      if (trigger) trigger.click();
    }
  }
  function quickAdd(button) {
    if (button.dataset.adding === '1') return;
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;
    button.dataset.adding = '1';
    var labelEl = button.querySelector('.modulo-sv__atc-label');
    var original = labelEl ? labelEl.textContent : '';
    var addedLabel = button.getAttribute('data-added-label') || 'Added';

    fetch(cartAddUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart-add-failed');
      return r.json();
    }).then(function () {
      button.classList.add('is-added');
      if (labelEl) labelEl.textContent = addedLabel;
      setTimeout(function () {
        button.classList.remove('is-added');
        if (labelEl) labelEl.textContent = original;
      }, 1800);
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      openCartDrawer();
    }).catch(function () {
      if (labelEl) {
        labelEl.textContent = 'Error';
        setTimeout(function () { labelEl.textContent = original; }, 1800);
      }
    }).finally(function () {
      button.dataset.adding = '0';
    });
  }

  /* ─── Helpers ─── */
  function fmtDuration(s) {
    s = Math.round(s);
    var m = Math.floor(s / 60);
    var ss = s % 60;
    return m + ':' + (ss < 10 ? '0' : '') + ss;
  }
  function clearNode(n) { while (n && n.firstChild) n.removeChild(n.firstChild); }
  // Render a price as text nodes (+ optional <s> compare-at) without innerHTML.
  function setPriceNode(node, price, compare) {
    if (!node) return;
    clearNode(node);
    node.appendChild(document.createTextNode(price));
    if (compare) {
      node.appendChild(document.createTextNode(' '));
      var s = document.createElement('s');
      s.textContent = compare;
      node.appendChild(s);
    }
  }
  function closePopovers(scope) {
    var open = scope.querySelectorAll('.modulo-sv__buy-wrap.is-open');
    for (var i = 0; i < open.length; i++) {
      var wrap = open[i];
      var t = wrap.querySelector('[data-modulo-sv-vtoggle]');
      var focusInside = wrap.contains(document.activeElement);
      wrap.classList.remove('is-open');
      if (t) { t.setAttribute('aria-expanded', 'false'); if (focusInside) t.focus(); }
    }
  }

  function initSection(el) {
    if (!el || el.dataset.moduloSvInit === '1') return; // idempotent — editor re-renders
    el.dataset.moduloSvInit = '1';

    var track = el.querySelector('[data-modulo-sv-track]');
    var prev = el.querySelector('[data-modulo-sv-prev]');
    var next = el.querySelector('[data-modulo-sv-next]');
    var lightbox = el.querySelector('[data-modulo-sv-lightbox]');
    var lightboxBody = lightbox && lightbox.querySelector('[data-modulo-sv-lightbox-body]');
    var lightboxClose = lightbox && lightbox.querySelector('[data-modulo-sv-lightbox-close]');

    /* ── Rail: scroll-snap arrows ── */
    function step() {
      if (!track) return 0;
      var card = track.querySelector('.modulo-sv__card');
      if (!card) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
      return card.getBoundingClientRect().width + gap;
    }
    function maxScroll() { return track.scrollWidth - track.clientWidth - 2; }
    function updateArrows() {
      if (!track) return;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll();
    }
    function onPrev() { track.scrollBy({ left: -step() }); }
    function onNext() { track.scrollBy({ left: step() }); }
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);

    var raf = null;
    function onScroll() {
      if (raf) return;
      raf = window.requestAnimationFrame(function () { raf = null; updateArrows(); });
    }
    if (track) track.addEventListener('scroll', onScroll);

    var resizeT;
    function onResize() {
      window.clearTimeout(resizeT);
      resizeT = window.setTimeout(updateArrows, 100);
    }
    window.addEventListener('resize', onResize);

    /* ── In-view muted autoplay (uploaded videos only) ── */
    var io = null;
    if (el.getAttribute('data-sv-autoplay') === 'true' && !REDUCE && 'IntersectionObserver' in window) {
      var vids = el.querySelectorAll('[data-modulo-sv-video]');
      if (vids.length) {
        io = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            var v = entries[i].target;
            var card = v.closest('[data-modulo-sv-card]');
            if (entries[i].isIntersecting) {
              if (v.preload !== 'metadata') v.preload = 'metadata';
              var p = v.play();
              if (p && p.catch) p.catch(function () {});
              if (card) card.classList.add('is-playing');
            } else {
              v.pause();
              if (card) card.classList.remove('is-playing');
            }
          }
        }, { threshold: 0.5 });
        for (var vi = 0; vi < vids.length; vi++) io.observe(vids[vi]);
      }
    }

    /* ── Duration pill (uploaded videos; computed client-side) ── */
    var dvids = el.querySelectorAll('[data-modulo-sv-video]');
    for (var di = 0; di < dvids.length; di++) {
      (function (v) {
        var card = v.closest('[data-modulo-sv-card]');
        var durEl = card && card.querySelector('[data-modulo-sv-dur]');
        if (!durEl) return;
        function setDur() {
          if (v.duration && isFinite(v.duration) && v.duration > 0) {
            durEl.textContent = fmtDuration(v.duration);
            durEl.hidden = false;
            v.removeEventListener('loadedmetadata', setDur); // self-cleaning
          }
        }
        if (v.readyState >= 1) setDur();
        else v.addEventListener('loadedmetadata', setDur);
      })(dvids[di]);
    }

    /* ── Lightbox ── */
    function openModal(card) {
      if (!lightbox || !lightboxBody) return;
      var cardVideo = card.querySelector('[data-modulo-sv-video]');
      if (cardVideo) cardVideo.pause(); // stop the muted preview decoding behind the modal
      var source = card.querySelector('[data-modulo-sv-source]');
      clearNode(lightboxBody);
      if (source) {
        // clone our own server-rendered (escaped/sanitized) nodes — no innerHTML re-parse
        for (var c = 0; c < source.children.length; c++) {
          lightboxBody.appendChild(source.children[c].cloneNode(true));
        }
      }
      // Sync the cloned modal to the variant the shopper picked on the card.
      var cardAdd = card.querySelector('[data-modulo-sv-add]');
      var vid = cardAdd && cardAdd.getAttribute('data-variant-id');
      if (vid) {
        var opts = lightboxBody.querySelectorAll('.modulo-sv__modal-variant');
        var chosen = null;
        for (var oi = 0; oi < opts.length; oi++) {
          var match = opts[oi].getAttribute('data-variant-id') === vid;
          opts[oi].setAttribute('aria-pressed', match ? 'true' : 'false');
          if (match) chosen = opts[oi];
        }
        var mAdd = lightboxBody.querySelector('[data-modulo-sv-add]');
        if (mAdd) mAdd.setAttribute('data-variant-id', vid);
        var mPriceEl = lightboxBody.querySelector('[data-modulo-sv-modal-price]');
        if (chosen && mPriceEl && chosen.getAttribute('data-price')) {
          setPriceNode(mPriceEl, chosen.getAttribute('data-price'), chosen.getAttribute('data-compare-price'));
        }
      }
      var iframe = lightboxBody.querySelector('iframe[data-src]');
      if (iframe) iframe.src = iframe.getAttribute('data-src');
      var v = lightboxBody.querySelector('video');
      if (v) { var pp = v.play(); if (pp && pp.catch) pp.catch(function () {}); }
      // Distinct accessible name per clip.
      var titleEl = lightboxBody.querySelector('.modulo-sv__modal-title');
      if (titleEl && titleEl.textContent.trim()) lightbox.setAttribute('aria-label', titleEl.textContent.trim());
      lightbox._prevFocus = document.activeElement;
      lightbox._prevOverflow = document.body.style.overflow;
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (lightboxClose) lightboxClose.focus();
    }
    function closeModal() {
      if (!lightbox) return;
      lightbox.setAttribute('aria-hidden', 'true');
      clearNode(lightboxBody); // stops video + frees iframe
      document.body.style.overflow = lightbox._prevOverflow || '';
      if (lightbox._prevFocus && typeof lightbox._prevFocus.focus === 'function') lightbox._prevFocus.focus();
    }
    function modalOpen() { return lightbox && lightbox.getAttribute('aria-hidden') === 'false'; }
    function focusables() {
      return lightbox.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, iframe, video, audio, [tabindex]:not([tabindex="-1"])');
    }
    if (lightboxClose) lightboxClose.addEventListener('click', closeModal);
    if (lightbox) lightbox.addEventListener('click', function (e) { if (e.target === lightbox) closeModal(); });

    /* ── Delegated clicks (cards + lightbox both live inside `el`) ── */
    function onClick(e) {
      // sound toggle
      var snd = e.target.closest('[data-modulo-sv-sound]');
      if (snd && el.contains(snd)) {
        var scard = snd.closest('[data-modulo-sv-card]');
        var sv = scard && scard.querySelector('[data-modulo-sv-video]');
        if (sv) {
          sv.muted = !sv.muted;
          snd.setAttribute('aria-pressed', sv.muted ? 'false' : 'true');
          snd.setAttribute('aria-label', sv.muted ? 'Unmute video' : 'Mute video');
          if (!sv.muted) { var sp = sv.play(); if (sp && sp.catch) sp.catch(function () {}); }
        }
        return;
      }
      // variant popover toggle
      var vtog = e.target.closest('[data-modulo-sv-vtoggle]');
      if (vtog && el.contains(vtog)) {
        var wrap = vtog.closest('[data-modulo-sv-buywrap]');
        var willOpen = !wrap.classList.contains('is-open');
        closePopovers(el);
        if (willOpen) { wrap.classList.add('is-open'); vtog.setAttribute('aria-expanded', 'true'); }
        return;
      }
      // card variant choose
      var cvar = e.target.closest('.modulo-sv__variant');
      if (cvar && el.contains(cvar)) {
        if (cvar.disabled) return;
        var menu = cvar.closest('[data-modulo-sv-variants]');
        var items = menu.querySelectorAll('.modulo-sv__variant');
        for (var i = 0; i < items.length; i++) items[i].setAttribute('aria-pressed', items[i] === cvar ? 'true' : 'false');
        var bw = cvar.closest('[data-modulo-sv-buywrap]');
        var atc = bw.querySelector('[data-modulo-sv-add]');
        if (atc) atc.setAttribute('data-variant-id', cvar.getAttribute('data-variant-id'));
        var cardEl = cvar.closest('[data-modulo-sv-card]');
        var cp = cardEl && cardEl.querySelector('[data-modulo-sv-chip-price]');
        if (cp && cvar.getAttribute('data-price')) setPriceNode(cp, cvar.getAttribute('data-price'), cvar.getAttribute('data-compare-price'));
        var vtl = bw.querySelector('.modulo-sv__vt-label');
        var nameEl = cvar.querySelector('.modulo-sv__variant-name');
        if (vtl && nameEl) vtl.textContent = nameEl.textContent;
        var moveFocus = bw.contains(document.activeElement);
        bw.classList.remove('is-open');
        var tg = bw.querySelector('[data-modulo-sv-vtoggle]');
        if (tg) { tg.setAttribute('aria-expanded', 'false'); if (moveFocus) tg.focus(); }
        return;
      }
      // modal variant choose
      var mvar = e.target.closest('.modulo-sv__modal-variant');
      if (mvar && el.contains(mvar)) {
        if (mvar.disabled) return;
        var mopts = mvar.parentNode.querySelectorAll('.modulo-sv__modal-variant');
        for (var m = 0; m < mopts.length; m++) mopts[m].setAttribute('aria-pressed', mopts[m] === mvar ? 'true' : 'false');
        var matc = lightbox.querySelector('[data-modulo-sv-add]');
        if (matc) matc.setAttribute('data-variant-id', mvar.getAttribute('data-variant-id'));
        var mprice = lightbox.querySelector('[data-modulo-sv-modal-price]');
        if (mprice && mvar.getAttribute('data-price')) setPriceNode(mprice, mvar.getAttribute('data-price'), mvar.getAttribute('data-compare-price'));
        return;
      }
      // add to cart (card or modal)
      var addBtn = e.target.closest('[data-modulo-sv-add]');
      if (addBtn && el.contains(addBtn)) {
        if (!addBtn.disabled) quickAdd(addBtn);
        return;
      }
      // open lightbox
      var opener = e.target.closest('[data-modulo-sv-open]');
      if (opener && el.contains(opener)) {
        var ocard = opener.closest('[data-modulo-sv-card]');
        if (ocard) openModal(ocard);
        return;
      }
    }
    el.addEventListener('click', onClick);

    /* ── Keyboard: ESC + focus trap (modal) / ESC (popover) ── */
    function onKeydown(e) {
      if (modalOpen()) {
        if (e.key === 'Escape') { closeModal(); return; }
        if (e.key === 'Tab') {
          var f = focusables();
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
        return;
      }
      if (e.key === 'Escape') closePopovers(el);
    }
    document.addEventListener('keydown', onKeydown);

    /* ── Outside click closes the variant popover ── */
    function onDocClick(e) {
      if (!e.target.closest('[data-modulo-sv-buywrap]')) closePopovers(el);
    }
    document.addEventListener('click', onDocClick);

    /* ── Teardown (theme editor) ── */
    el.moduloSvTeardown = function () {
      if (io) io.disconnect();
      window.removeEventListener('resize', onResize);
      if (track) track.removeEventListener('scroll', onScroll);
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
      el.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeydown);
      document.removeEventListener('click', onDocClick);
      if (modalOpen()) closeModal();
      el.dataset.moduloSvInit = '';
    };

    updateArrows();
  }

  function initAll() {
    if (window.moduloLazyInit) {
      window.moduloLazyInit('[data-modulo-sv]', initSection);
    } else {
      var els = document.querySelectorAll('[data-modulo-sv]');
      for (var i = 0; i < els.length; i++) initSection(els[i]);
    }
  }

  function sectionTarget(e) {
    var t = e.target;
    if (!t || !t.querySelector) return null;
    if (t.matches && t.matches('[data-modulo-sv]')) return t;
    return t.querySelector('[data-modulo-sv]');
  }
  document.addEventListener('shopify:section:load', function (e) {
    var el = sectionTarget(e);
    if (el) initSection(el);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = sectionTarget(e);
    if (el && typeof el.moduloSvTeardown === 'function') el.moduloSvTeardown();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
